import { toast } from 'react-toastify';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import PdfAnnotationModal from '@/components/PdfAnnotationModal';
import { FaEdit } from 'react-icons/fa';
import { 
    getAllContracts, 
    createContract, 
    updateContract, 
    validateContract,
    getAllCounterparties,
    getAllProperties,
    uploadContractDocument,
    getContractDocuments,
    deleteContractDocument
} from '@/services/marketingService';
import * as Sentry from '@sentry/react';

const ContractsPage = () => {
    const [contracts, setContracts] = useState([]);
    const [counterparties, setCounterparties] = useState([]);
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCounterparty, setFilterCounterparty] = useState('all');
    const [filterProperty, setFilterProperty] = useState('all');
    const [validationMessage, setValidationMessage] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [showPdfAnnotationModal, setShowPdfAnnotationModal] = useState(false);
    const [selectedPdfDocument, setSelectedPdfDocument] = useState(null);
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        counterpartyId: '',
        propertyId: '',
        tenantCode: '',
        contractStartDate: '',
        contractEndDate: '',
        notes: '',
        isActive: true
    });

    const { apiFetch } = useAuth();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [contractsData, counterpartiesData, propertiesData] = await Promise.all([
                getAllContracts(apiFetch),
                getAllCounterparties(apiFetch),
                getAllProperties(apiFetch)
            ]);
            setContracts(contractsData);
            setCounterparties(counterpartiesData);
            setProperties(propertiesData);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (contract = null) => {
        setValidationMessage(null);
        setDocuments([]);
        if (contract) {
            setSelectedContract(contract);
            setFormData({
                counterpartyId: contract.counterpartyId || '',
                propertyId: contract.propertyId || '',
                tenantCode: contract.tenantCode || '',
                contractStartDate: contract.contractStartDate?.split('T')[0] || '',
                contractEndDate: contract.contractEndDate?.split('T')[0] || '',
                notes: contract.notes || '',
                isActive: contract.isActive ?? true
            });
            // Load documents for this contract
            loadDocuments(contract.id);
        } else {
            setSelectedContract(null);
            setFormData({
                counterpartyId: '',
                propertyId: '',
                tenantCode: '',
                contractStartDate: '',
                contractEndDate: '',
                notes: '',
                isActive: true
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedContract(null);
        setValidationMessage(null);
        setDocuments([]);
    };

    const loadDocuments = async (contractId) => {
        try {
            const docs = await getContractDocuments(apiFetch, contractId);
            setDocuments(docs);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load documents');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!selectedContract?.id) {
            toast.error('Please save the contract before uploading documents');
            return;
        }

        setUploadingFile(true);
        try {
            await uploadContractDocument(apiFetch, selectedContract.id, file);
            toast.success('Document uploaded successfully');
            await loadDocuments(selectedContract.id);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to upload document');
        } finally {
            setUploadingFile(false);
        }
    };

    const handleDeleteDocument = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this document?')) {
            return;
        }

        try {
            await deleteContractDocument(apiFetch, selectedContract.id, fileId);
            toast.success('Document deleted successfully');
            await loadDocuments(selectedContract.id);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to delete document');
        }
    };

    const isPdfFile = (doc) => {
        return doc.contentType?.toLowerCase().includes('pdf') || 
               doc.fileName?.toLowerCase().endsWith('.pdf');
    };

    const handleAnnotatePdf = (doc) => {
        // Convert document format to match attachment format expected by PdfAnnotationModal
        const attachmentFormat = {
            attachmentId: doc.fileId,
            fileName: doc.fileName,
            contentType: doc.contentType,
            downloadUrl: doc.downloadUrl,
            fileSize: doc.fileSize
        };
        setSelectedPdfDocument(attachmentFormat);
        setShowPdfAnnotationModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleValidate = async () => {
        if (!formData.counterpartyId || !formData.propertyId || 
            !formData.contractStartDate || !formData.contractEndDate) {
            toast.error('Please fill in all required fields for validation');
            return;
        }

        try {
            const result = await validateContract(
                apiFetch,
                formData.counterpartyId,
                formData.propertyId,
                formData.contractStartDate,
                formData.contractEndDate
            );
            
            if (result.isValid) {
                setValidationMessage({ type: 'success', text: 'Contract dates are valid!' });
            } else {
                setValidationMessage({ type: 'error', text: result.message || 'Contract validation failed' });
            }
        } catch (error) {
            Sentry.captureException(error);
            setValidationMessage({ type: 'error', text: error.message || 'Validation failed' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.counterpartyId || !formData.propertyId || 
            !formData.contractStartDate || !formData.contractEndDate) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (new Date(formData.contractEndDate) <= new Date(formData.contractStartDate)) {
            toast.error('End date must be after start date');
            return;
        }

        setIsLoading(true);
        try {
            if (selectedContract) {
                await updateContract(apiFetch, selectedContract.contractId, formData);
                toast.success('Contract updated successfully');
            } else {
                await createContract(apiFetch, formData);
                toast.success('Contract created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to save contract');
        } finally {
            setIsLoading(false);
        }
    };

    const getCounterpartyName = (id) => {
        const cp = counterparties.find(c => c.Id === id);
        return cp?.CompanyName || 'Unknown';
    };

    const getPropertyName = (id) => {
        const prop = properties.find(p => (p.propertyID ?? p.PropertyID) === id);
        return prop?.propertyName || prop?.PropertyName || 'Unknown';
    };

    const isContractExpired = (endDate) => {
        return new Date(endDate) < new Date();
    };

    const filteredContracts = contracts.filter(contract => {
        const counterpartyName = getCounterpartyName(contract.counterpartyId);
        const propertyName = getPropertyName(contract.propertyId);
        const matchesSearch = counterpartyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            propertyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            contract.tenantCode?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCounterparty = filterCounterparty === 'all' || 
                                   contract.counterpartyId === parseInt(filterCounterparty);
        
        const matchesProperty = filterProperty === 'all' || 
                              contract.propertyId === parseInt(filterProperty);

        return matchesSearch && matchesCounterparty && matchesProperty;
    });

    return (
        <div className="csi-container">
            <h1>Marketing Contracts</h1>

            <div className="toolbar">
                <div className="search-filter-group">
                    <input
                        type="text"
                        placeholder="Search contracts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-control search-input"
                    />
                    <select
                        value={filterCounterparty}
                        onChange={(e) => setFilterCounterparty(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All Counterparties</option>
                        {counterparties.map((cp, index) => (
                            <option key={cp.Id ?? `cp-${index}`} value={cp.Id}>
                                {cp.CompanyName}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filterProperty}
                        onChange={(e) => setFilterProperty(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All Properties</option>
                        {properties.map((prop, index) => (
                            <option key={prop.propertyID ?? prop.PropertyID ?? `prop-${index}`} value={prop.propertyID ?? prop.PropertyID}>
                                {prop.propertyName || prop.PropertyName}
                            </option>
                        ))}
                    </select>
                </div>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    Add Contract
                </button>
            </div>

            {isLoading ? (
                <div className="loading">Loading contracts...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Counterparty</th>
                                <th>Property</th>
                                <th>Tenant Code</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredContracts.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        No contracts found
                                    </td>
                                </tr>
                            ) : (
                                filteredContracts.map(contract => (
                                    <tr key={contract.contractId}>
                                        <td>{getCounterpartyName(contract.counterpartyId)}</td>
                                        <td>{getPropertyName(contract.propertyId)}</td>
                                        <td>{contract.tenantCode || '-'}</td>
                                        <td>{new Date(contract.contractStartDate).toLocaleDateString()}</td>
                                        <td>{new Date(contract.contractEndDate).toLocaleDateString()}</td>
                                        <td>
                                            {!contract.isActive ? (
                                                <span className="badge badge-secondary">Inactive</span>
                                            ) : isContractExpired(contract.contractEndDate) ? (
                                                <span className="badge badge-danger">Expired</span>
                                            ) : (
                                                <span className="badge badge-success">Active</span>
                                            )}
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleOpenModal(contract)}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>{selectedContract ? 'Edit Contract' : 'Add Contract'}</h3>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="counterpartyId">Counterparty *</label>
                                    <select
                                        id="counterpartyId"
                                        name="counterpartyId"
                                        value={formData.counterpartyId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">Select Counterparty</option>
                                        {counterparties.filter(cp => cp.IsActive).map((cp, index) => (
                                            <option key={cp.Id ?? `modal-cp-${index}`} value={cp.Id}>
                                                {cp.CompanyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="propertyId">Property *</label>
                                    <select
                                        id="propertyId"
                                        name="propertyId"
                                        value={formData.propertyId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">Select Property</option>
                                        {properties.map((prop, index) => (
                                            <option key={prop.propertyID ?? prop.PropertyID ?? `modal-prop-${index}`} value={prop.propertyID ?? prop.PropertyID}>
                                                {prop.propertyName || prop.PropertyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="tenantCode">Tenant Code</label>
                                    <input
                                        type="text"
                                        id="tenantCode"
                                        name="tenantCode"
                                        value={formData.tenantCode}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="contractStartDate">Start Date *</label>
                                    <input
                                        type="date"
                                        id="contractStartDate"
                                        name="contractStartDate"
                                        value={formData.contractStartDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="contractEndDate">End Date *</label>
                                    <input
                                        type="date"
                                        id="contractEndDate"
                                        name="contractEndDate"
                                        value={formData.contractEndDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary"
                                        onClick={handleValidate}
                                    >
                                        Validate Contract
                                    </button>
                                    {validationMessage && (
                                        <div className={`alert ${validationMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`}
                                             style={{ marginTop: '10px' }}>
                                            {validationMessage.text}
                                        </div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="notes">Notes</label>
                                    <textarea
                                        id="notes"
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        rows="3"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                        />
                                        {' '}Active
                                    </label>
                                </div>

                                {selectedContract && (
                                    <div className="form-group">
                                        <label>Contract Documents</label>
                                        <div className="documents-section" style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '15px', marginTop: '10px' }}>
                                            <div className="documents-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <h4 style={{ margin: 0, fontSize: '16px' }}>Documents</h4>
                                                <label className="btn btn-sm btn-primary" style={{ cursor: 'pointer', margin: 0 }}>
                                                    <input
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileUpload}
                                                        style={{ display: 'none' }}
                                                        disabled={uploadingFile}
                                                    />
                                                    {uploadingFile ? 'Uploading...' : 'Upload Document'}
                                                </label>
                                            </div>
                                            {documents.length === 0 ? (
                                                <p style={{ color: '#666', margin: '10px 0', fontSize: '14px' }}>No documents uploaded</p>
                                            ) : (
                                                <ul className="documents-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {documents.map(doc => (
                                                        <li key={doc.fileId} style={{ 
                                                            display: 'flex', 
                                                            justifyContent: 'space-between', 
                                                            alignItems: 'center', 
                                                            padding: '10px', 
                                                            borderBottom: '1px solid #eee',
                                                            fontSize: '14px'
                                                        }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: '500' }}>{doc.fileName}</div>
                                                                <div style={{ color: '#666', fontSize: '12px' }}>
                                                                    {doc.fileSizeFormatted} • {new Date(doc.uploadedDate).toLocaleDateString()} • {doc.uploadedByUserName}
                                                                </div>
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                                {isPdfFile(doc) && (
                                                                    <button 
                                                                        className="btn btn-sm btn-info"
                                                                        onClick={() => handleAnnotatePdf(doc)}
                                                                        title="Annotate PDF"
                                                                    >
                                                                        <FaEdit size={14} /> Annotate
                                                                    </button>
                                                                )}
                                                                <a 
                                                                    href={doc.downloadUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-sm btn-secondary"
                                                                >
                                                                    Download
                                                                </a>
                                                                <button 
                                                                    onClick={() => handleDeleteDocument(doc.fileId)}
                                                                    className="btn btn-sm btn-danger"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseModal}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showPdfAnnotationModal && selectedPdfDocument && (
                <PdfAnnotationModal
                    isOpen={showPdfAnnotationModal}
                    onClose={() => {
                        setShowPdfAnnotationModal(false);
                        setSelectedPdfDocument(null);
                    }}
                    attachment={selectedPdfDocument}
                />
            )}
</div>
    );
};

export default ContractsPage;
